import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getAdminUserDetail } from "../../api/admin.api";
import { isUnauthorizedError } from "../../api/http";
import AdminBreadcrumbs from "../../components/admin/AdminBreadcrumbs";
import EmptyState from "../../components/ui/EmptyState";
import ErrorState from "../../components/ui/ErrorState";
import LoadingState from "../../components/ui/LoadingState";
import AdminLayout from "../../layouts/AdminLayout";
import normalizeApiError from "../../utils/normalizeApiError";

const formatTimestamp = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const pad = (num) => String(num).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export default function AdminUserDetail() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchUser = async () => {
    try {
      setLoading(true);
      setError(null);
      setNotFound(false);
      const response = await getAdminUserDetail(id);
      setUser(response || null);
    } catch (err) {
      if (isUnauthorizedError(err)) {
        setError(normalizeApiError(err));
        return;
      }
      const code = err?.response?.data?.code;
      if (code === "INVALID_USER_ID") {
        setNotFound(true);
        return;
      }
      setError(normalizeApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [id]);

  const detailRows = useMemo(() => {
    if (!user) return [];
    return [
      { label: "ID", value: user.id },
      { label: "Email", value: user.email },
      { label: "Phone", value: user.phone },
      { label: "Verified", value: user.isVerified ? "Yes" : "No" },
      { label: "Active", value: user.isActive ? "Yes" : "No" },
      { label: "Deleted", value: user.isDeleted ? "Yes" : "No" },
      {
        label: "Roles",
        value: Array.isArray(user.roles) ? user.roles.join(", ") : "—",
      },
      { label: "Created", value: formatTimestamp(user.createdAt) },
      { label: "Updated", value: formatTimestamp(user.updatedAt) },
    ];
  }, [user]);

  return (
    <AdminLayout title="Admin User Detail">
      <div className="space-y-4">
        <div className="card p-4">
          <AdminBreadcrumbs />
        </div>
        {loading ? (
          <LoadingState />
        ) : notFound ? (
          <EmptyState
            title="User not found"
            message="This user is not available or has been removed."
            testId="admin-empty-user-detail"
          />
        ) : error ? (
          <ErrorState error={error} onRetry={fetchUser} />
        ) : (
          <div className="card p-6 text-sm text-slate-600">
            {user ? (
              <div className="grid gap-3 md:grid-cols-2">
                {detailRows.map((row) => (
                  <div key={row.label} className="rounded-lg border border-slate-100 p-3">
                    <p className="text-xs text-slate-400">{row.label}</p>
                    <p className="mt-1 text-sm text-slate-900">
                      {row.value || "—"}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              "No user data."
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
